import { Bell, Plus, Trash2, AlertTriangle, Clock } from 'lucide-react';
import { useLanguage } from '../../../../features/language/useLanguage';
import type { ReminderOffset, ReminderUnit } from '../../../../types/notification';

const PRESETS: ReminderOffset[] = [
  { value: 15, unit: 'minutes' },
  { value: 1, unit: 'hours' },
  { value: 1, unit: 'days' },
];

interface ReminderScheduleEditorProps {
  reminders: ReminderOffset[];
  max: number;
  isEnabled: boolean;
  validationError: string | null;
  onAdd: (preset?: ReminderOffset) => void;
  onUpdate: (index: number, patch: Partial<ReminderOffset>) => void;
  onRemove: (index: number) => void;
}

/** Editable list of reminder offsets, with quick-add presets and validation feedback. */
export function ReminderScheduleEditor({
  reminders,
  max,
  isEnabled,
  validationError,
  onAdd,
  onUpdate,
  onRemove,
}: ReminderScheduleEditorProps) {
  const { t } = useLanguage();

  const unitLabel = (unit: ReminderUnit) => {
    if (unit === 'minutes') return t('notifications.settings.unitMinutes', undefined, 'minutes');
    if (unit === 'hours') return t('notifications.settings.unitHours', undefined, 'hours');
    return t('notifications.settings.unitDays', undefined, 'days');
  };

  return (
    <div className={`px-4 sm:px-6 pt-5 pb-2 transition-opacity ${!isEnabled ? 'opacity-70' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-gray-400" />
          <h4 className="text-sm font-medium text-gray-900">
            {t('notifications.settings.listHeading', undefined, 'Reminder schedule')}
          </h4>
          <span className="text-xs text-gray-500 tabular-nums">
            {reminders.length}/{max}
          </span>
        </div>
      </div>

      {reminders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-6 text-center">
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm">
            <Bell className="h-5 w-5" />
          </div>
          <p className="text-sm text-gray-600">
            {t(
              'notifications.settings.emptyState',
              undefined,
              'No reminders configured yet. Add one below to get notified before a roast starts.'
            )}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-test-id="notification-reminder-list">
          {reminders.map((r, i) => (
            <li
              key={i}
              className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 hover:border-gray-300 transition-colors"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600 text-xs font-semibold tabular-nums">
                {i + 1}
              </span>
              <input
                type="number"
                min={1}
                value={r.value}
                aria-label={t('notifications.settings.valueLabel', undefined, 'Value')}
                onChange={(e) => {
                  const val = e.target.value;
                  onUpdate(i, { value: val === '' ? ('' as unknown as number) : Number(val) });
                }}
                className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 tabular-nums"
                placeholder="1"
              />
              <select
                value={r.unit}
                aria-label={t('notifications.settings.unitLabel', undefined, 'Unit')}
                onChange={(e) => onUpdate(i, { unit: e.target.value as ReminderUnit })}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="minutes">{t('notifications.settings.unitMinutes', undefined, 'minutes')}</option>
                <option value="hours">{t('notifications.settings.unitHours', undefined, 'hours')}</option>
                <option value="days">{t('notifications.settings.unitDays', undefined, 'days')}</option>
              </select>
              <span className="hidden sm:inline text-sm text-gray-500 truncate">
                {t('notifications.settings.beforeStart', undefined, 'before start')}
              </span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label={t('notifications.settings.removeReminder', undefined, 'Remove reminder')}
                className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-300"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add + presets */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onAdd()}
          disabled={reminders.length >= max}
          data-test-id="notification-add-reminder"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="h-4 w-4" />
          {reminders.length >= max
            ? t('notifications.settings.maxReached', undefined, `Max ${max} reminders`)
            : t('notifications.settings.addReminder', undefined, 'Add reminder')}
        </button>
        {reminders.length < max && (
          <>
            <span className="text-xs text-gray-400 ml-1">
              {t('notifications.settings.quickAdd', undefined, 'Quick add:')}
            </span>
            {PRESETS.map((p) => (
              <button
                key={`${p.value}-${p.unit}`}
                type="button"
                onClick={() => onAdd(p)}
                className="inline-flex items-center px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-colors"
              >
                {p.value} {unitLabel(p.unit)}
              </button>
            ))}
          </>
        )}
      </div>

      {validationError && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{validationError}</p>
        </div>
      )}
    </div>
  );
}
